import { formatInTimeZone } from 'date-fns-tz'

/**
 * Parse a YYYY-MM-DD string as a local-time Date.
 *
 * `new Date('2026-03-04')` parses as UTC midnight — in US timezones (UTC-5/6)
 * this shifts to March 3 evening, causing off-by-one day errors.
 * This helper splits the parts and constructs a local-midnight Date instead.
 */
export const parseLocalDate = (dateStr: string): Date => {
  const [year, month, day] = dateStr.split('-').map(Number)
  return new Date(year, month - 1, day)
}

/**
 * Format a Date to a YYYY-MM-DD string using local time.
 */
export const toDateString = (date: Date): string => {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/**
 * Today's calendar date at `timezone`, as a YYYY-MM-DD string.
 *
 * "Today" is not a global fact: at 23:30 in Chicago it is already tomorrow in
 * New York. Anything that means "today at the branch" has to read the clock in
 * that branch's timezone rather than the browser's, or a dispatcher working a
 * branch in another zone — or anyone near midnight — lands on the wrong day.
 */
export const todayStringInTimezone = (timezone: string): string => formatInTimeZone(new Date(), timezone, 'yyyy-MM-dd')

/**
 * Today's calendar date at `timezone`, as a local-midnight Date — the shape
 * the date pickers and `toDateString` expect.
 */
export const todayInTimezone = (timezone: string): Date => parseLocalDate(todayStringInTimezone(timezone))
